import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Briefcase, MapPin, Calendar, ChevronUp, ChevronDown } from "lucide-react"
import { TaggedExperience } from "./types"
import { Button } from "@/components/ui/button"
import { useState } from "react"

type WorkExperienceCardProps = {
  experience: TaggedExperience
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long" })
}

export default function WorkExperienceCard({ experience }: WorkExperienceCardProps) {
  const { workExperience, tags, role } = experience
  const [isExpanded, setIsExpanded] = useState(false)

  // Function to get a preview of the description (first 150 characters)
  const getDescriptionPreview = (description: string) => {
    if (!description) return ""
    if (description.length <= 175) return description
    return description.substring(0, 175) + "..."
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{workExperience.job_title}</CardTitle>
        <div className="flex items-center space-x-2 text-muted-foreground">
          <Briefcase className="w-4 h-4" />
          <span>{workExperience.company_name}</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {workExperience.location && (
            <div className="flex items-center space-x-2 text-sm">
              <MapPin className="w-4 h-4" />
              <span>{workExperience.location}</span>
            </div>
          )}
          <div className="flex items-center space-x-2 text-sm">
            <Calendar className="w-4 h-4" />
            <span>
              {formatDate(workExperience.start_date)} -{" "}
              {workExperience.end_date ? formatDate(workExperience.end_date) : "Present"}
            </span>
          </div>

          {workExperience.work_experience_description && (
            <div className="mt-2">
              <h4 className="font-semibold mb-1">Description:</h4>
              <div className="text-sm">
                <p className="whitespace-pre-line">
                  {isExpanded
                    ? workExperience.work_experience_description
                    : getDescriptionPreview(workExperience.work_experience_description)}
                </p>
                {workExperience.work_experience_description.length > 150 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-8 px-2 text-xs flex items-center"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        Show more
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="mt-2">
            <h4 className="font-semibold mb-1">Technologies:</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="bg-gray-100">{workExperience.main_technology}</Badge>
              {workExperience.secondary_technologies.map((tech, index) => (
                <Badge key={index} variant="outline">
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
          <div className="mt-2">
            <h4 className="font-semibold mb-1">Problem Spaces:</h4>
            <div className="flex flex-wrap gap-2">
              {tags.problem_spaces.map((space, index) => (
                <Badge key={index} variant="outline">
                  {space}
                </Badge>
              ))}
            </div>
          </div>
          {role && (
            <div className="mt-4 p-3 bg-gray-100 rounded-md">
              <h4 className="font-semibold mb-2">Role Match: {role.selected.role}</h4>
              <p className="text-sm">{role.selected.explanation}</p>
              <div className="mt-2">
                <h5 className="font-medium text-sm">Other Potential Roles:</h5>
                <ul className="mt-1 space-y-1">
                  {role.candidates
                    .filter((c) => c.role !== role.selected.role)
                    .map((candidate, index) => (
                      <li key={index} className="text-xs">
                        <span className="font-medium">{candidate.role}:</span> {candidate.explanation}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

